// React
import PropTypes from "prop-types";

// @mui material components
import {
  Card,
  Fade,
  Grid,
  Icon,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
  Select,
  Badge,
} from "@mui/material";

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { useContext, useEffect, useState } from "react";
import areasTableData from "../data/dataAreas";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "45%", sm: "45%", md: "45%" },
  overflowY: "auto",
};

export default function ModalAreas({ open, handleClose }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState("");

  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "DESC",
  });

  const [getAreas, { loading: loadingAreas, data: dataAreas }] = useLazyQuery(
    gql`
      query GetAreas($pagination: PaginationInput, $order: Order, $search: String, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        areaProductos(pagination: $pagination, order: $order, search: $search, filter: $filter) {
          id
          nombre
        }
      }
    `,
    {
      variables: {
        search: search || null,
        pagination: { page: page, limit: 10 },
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: null,
      },
      fetchPolicy: "network-only",
    }
  );

  const { columns, getRows } = areasTableData;
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);

  const [saveArea, { loading: loadingSaveArea, data: dataSaveArea }] = useMutation(
    gql`
      mutation SaveArea($input: AreaProductoInput!) {
        saveAreaProducto(input: $input) {
          id
          nombre
        }
      }
    `
  );

  //Render menu
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelected(item);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const refetchAreas = () => {
    getAreas()
      .then((res) => {
        if (res?.data && res.data.paginationInfo) {
          setPagesInfo(res.data.paginationInfo);
          setRows(getRows(res.data.areaProductos, handleClick));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const handleSaveArea = () => {
    if (!nombre.trim()) {
      handleSnackbar("El nombre es requerido", "error");
      return;
    }

    const input = {
      nombre: nombre.trim(),
    };

    if (editando && selected) {
      input.id = selected.id;
    }

    saveArea({ variables: { input } })
      .then(() => {
        handleSnackbar(
          editando ? "Área actualizada correctamente" : "Área creada correctamente",
          "success"
        );
        handleResetForm();
        refetchAreas();
      })
      .catch((error) => {
        console.error(error);
        handleSnackbar("Error al guardar el área", "error");
      });
  };

  const handleResetForm = () => {
    setShowForm(false);
    setEditando(false);
    setNombre("");
    setSelected(null);
  };

  const handleAddNew = () => {
    setEditando(false);
    setNombre("");
    setSelected(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditando(true);
    setNombre(item.nombre || "");
    setSelected(item);
    setShowForm(true);
    handleCloseMenu();
  };

  const renderMenu = () => (
    <Menu
      id="simple-menu"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <MenuItem onClick={() => handleEdit(selected)}>
        Editar
        <Icon sx={{ ml: 1 }} color="secondary">
          edit
        </Icon>
      </MenuItem>
    </Menu>
  );

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    const newTimeout = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 500);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  useEffect(() => {
    if (open) {
      refetchAreas();
    }
  }, [open, search, page, orderBy]);

  useEffect(() => {
    if (dataAreas && dataAreas.areaProductos) {
      setPagesInfo(dataAreas.paginationInfo);
      setRows(getRows(dataAreas.areaProductos, handleClick));
    }
  }, [dataAreas]);

  useEffect(() => {
    if (!open) {
      handleResetForm();
      setSearch("");
      setSearchInput("");
      setPage(1);
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox p={3}>
            {/* Header */}
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <SoftTypography variant="h6">
                {showForm ? (editando ? "Editar Área" : "Nueva Área") : "Áreas"}
              </SoftTypography>
              <SoftBox display="flex" alignItems="center" gap={1}>
                {showForm ? (
                  <>
                    <Tooltip title="Volver" placement="top">
                      <SoftButton color="secondary" circular iconOnly onClick={handleResetForm}>
                        <Icon>arrow_back</Icon>
                      </SoftButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <SoftBox mr={2}>
                      <SoftInput
                        placeholder="Buscar..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                        value={searchInput}
                      />
                    </SoftBox>
                    <Tooltip title="Agregar nueva área" placement="top">
                      <SoftButton color="primary" circular iconOnly onClick={handleAddNew}>
                        <Icon>add</Icon>
                      </SoftButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Cerrar" placement="top">
                  <SoftButton color="error" circular iconOnly onClick={handleClose}>
                    <Icon>close</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>
            </SoftBox>

            {/* Content */}
            {showForm ? (
              /* Form View */
              <SoftBox>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SoftBox mb={2}>
                      <SoftTypography variant="button" fontWeight="medium" color="text">
                        Nombre del área
                      </SoftTypography>
                    </SoftBox>
                    <SoftInput
                      placeholder="Ingrese el nombre del área"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SoftBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
                      <SoftButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleResetForm}
                        circular
                      >
                        Cancelar
                      </SoftButton>
                      <SoftButton
                        color="primary"
                        onClick={handleSaveArea}
                        disabled={loadingSaveArea}
                        circular
                      >
                        <Icon sx={{ mr: 1 }}>save</Icon>
                        {loadingSaveArea ? "Guardando..." : "Guardar"}
                      </SoftButton>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            ) : (
              /* Table View */
              <SoftBox>
                <SoftBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                    width: "100%",
                    minHeight: "300px",
                  }}
                >
                  {loadingAreas ? (
                    <Loading />
                  ) : dataAreas && dataAreas.areaProductos && dataAreas.areaProductos.length > 0 ? (
                    <>
                      <DataTable
                        columns={columns}
                        rows={rows}
                        order={orderBy}
                        setOrder={handleOrderBy}
                      />
                      {renderMenu()}
                    </>
                  ) : (
                    <SinDatos />
                  )}
                </SoftBox>

                {/* Pagination */}
                {!loadingAreas &&
                  dataAreas &&
                  dataAreas.areaProductos &&
                  dataAreas.areaProductos.length > 0 && (
                    <CustomPagination
                      loading={loadingAreas}
                      length={rows?.length}
                      total={pagesInfo?.total}
                      pages={pagesInfo?.pages}
                      page={page}
                      setPage={setPage}
                      noCard
                    />
                  )}
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAreas.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};
